import { ConfigProvider, DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import FullPageLoader from "../../../core/common/loader/FullPageLoader";
import LoadingModal from "../../../core/modals/loadingModal";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import {
  createOrUpdateNocPassport,
  getNocPassport,
} from "../../../core/data/redux/nocPassportSlice";
import type { AppDispatch, RootState } from "../../../core/data/redux/store";
import { all_routes } from "../../router/all_routes";
import { useT } from "../../../core/i18n";

interface PassportCreateProps { }

const PassportCreate = (props: PassportCreateProps) => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const dispatch = useDispatch<AppDispatch>();
  const { t, tBoth } = useT();
  const employeesState = useSelector(
    (state: RootState) => state.employeesState
  );
  const nocPassportState = useSelector(
    (state: RootState) => state.nocPassportState
  );
  const passportData = nocPassportState?.currentPassport;

  // Get current user data
  const user = employeesState?.data?.data;

  const [showChecklist, setShowChecklist] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    // Employee Details (to be filled from profile)
    name: "",
    designation: "",
    dateOfJoining: "",
    employmentStatus: "",
    department: "",
    contactNumber: "",
    email: "",
    dateOfBirth: "",
    employeeNo: "",

    // Application Details
    fatherName: "",
    placeOfPosting: "",
    presentAddress: "",
    permanentAddress: "",
    nocAppliedFor: "",
    passportNumber: "",

    // Visa specific fields
    passportType: "",
    placeOfVisitNOC: "",
    purposeOfVisit: "",
    tentativeDate: "",
    durationOfVisit: "",

    // Declaration fields
    pendingCase: "",
    dnARPending: "",
    vigilancePending: "",
    dnARStatus: "",
    dnARPenaltyEffecti: "",

    // Declaration checkbox
    declaration: false,
  });

  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isLoadingPassportData, setIsLoadingPassportData] =
    useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Processing...");
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [responseType, setResponseType] = useState<"success" | "error">("success");

  // Fetch user profile on component mount
  useEffect(() => {
    dispatch(getMyProfile({}));

    // Set a timeout to stop loading if API takes too long
    const timeout = setTimeout(() => {
      setIsLoadingProfile(false);
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [dispatch]);

  // Fetch passport data in edit mode
  useEffect(() => {
    if (id) {
      setShowChecklist(false);
      setIsLoadingPassportData(true);
      dispatch(getNocPassport({ id: id }));
    } else {
      setIsLoadingPassportData(false);
    }
  }, [dispatch, id]);
  useEffect(() => {
    if (user?.details) {
      const userDetails = user.details;

      setFormData((prev) => ({
        ...prev,
        name: `${userDetails.firstname || ""} ${userDetails.lsastname || ""
          }`.trim(),
        designation:
          userDetails.sap_department_designation_wings?.designation_name ||
          userDetails.designation_details?.designationName ||
          `Designation ID: ${userDetails.designationID || ""}`,
        dateOfJoining: userDetails.date_of_joining
          ? moment(userDetails.date_of_joining).format("DD-MM-YYYY")
          : "",
        employmentStatus:
          userDetails.employmentType || user.employmentType || user.status,
        department:
          userDetails.sap_department_designation_wings?.department_name ||
          userDetails.department_details?.departmentName ||
          `Department ID: ${userDetails.department || ""}`,
        contactNumber: userDetails.phone || "",
        email: userDetails.email || "",
        dateOfBirth: userDetails.birthdate
          ? moment(userDetails.birthdate).format("DD-MM-YYYY")
          : "",
        employeeNo: userDetails.empId || "",
      }));

      setIsLoadingProfile(false);
    } else if (employeesState?.loading === "fulfilled" && !user?.details) {
      // If API call completed but no user data, stop loading anyway
      setIsLoadingProfile(false);
    }
  }, [user, employeesState?.loading]);

  // Populate form data when passport data is loaded in edit mode
  useEffect(() => {
    if (passportData && isEditMode) {
      setFormData((prev) => ({
        ...prev,
        fatherName: passportData.fatherName || "",
        placeOfPosting: passportData.placeOfPosting || "",
        presentAddress: passportData.presentAddress || "",
        permanentAddress: passportData.permanentAddress || "",
        nocAppliedFor: passportData.nOCAppliedFor || "",
        passportNumber: passportData.passportNumber || "",
        passportType: passportData.passportType || "",
        placeOfVisitNOC: passportData.placeOfVisitNOC || "",
        purposeOfVisit: passportData.purposeOfVisit || "",
        tentativeDate: passportData.tentativeDate || "",
        durationOfVisit: passportData.durationOfVisit || "",
        pendingCase: passportData.pendingCase === "Y" ? "Yes" : "No",
        dnARPending: passportData.dnARPending === "Y" ? "Yes" : "No",
        vigilancePending: passportData.vigilancePending === "Y" ? "Yes" : "No",
        dnARStatus: passportData.dnARStatus || "",
        dnARPenaltyEffecti: passportData.dnARPenaltyEffecti || "",
        declaration: false, // Always require re-acceptance
      }));
      setIsLoadingPassportData(false);
    } else if (nocPassportState?.loading === false && isEditMode) {
      // If loading completed but no data, stop loading
      setIsLoadingPassportData(false);
    }
  }, [passportData, isEditMode, nocPassportState?.loading]);

  useEffect(() => {
    if (id) {
      setShowChecklist(false);
      dispatch(getNocPassport({ id: id }));
    }
  }, [dispatch, id]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      // Validate Father's Name - only allow alphabetic characters and spaces
      if (name === "fatherName") {
        // Allow only letters (a-z, A-Z) and spaces
        const regex = /^[a-zA-Z\s]*$/;
        if (!regex.test(value)) {
          // If invalid characters are present, show error and don't update
          toast.error("Father's Name can only contain letters and spaces");
          return;
        }
      }

      // Validate Contact Number - only allow digits, max 10
      if (name === "contactNumber") {
        const cleaned = value.replace(/\D/g, "").slice(0, 10);
        setFormData((prev) => ({
          ...prev,
          contactNumber: cleaned,
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.declaration) {
      toast.error("Please accept the declaration before submitting.");
      return;
    }

    if (!formData.contactNumber || !/^\d{10}$/.test(formData.contactNumber)) {
      toast.error("Contact number must be exactly 10 digits");
      return;
    }

    setIsSubmitting(true);
    setShowLoadingModal(true);
    setLoadingMessage(
      isEditMode ? "Updating application..." : "Submitting application..."
    );

    try {
      const currentDateYMD = moment().format("YYYY-MM-DD");

      const getFirstLetter = (value: string): string => {
        if (!value) return "";
        const trimmed = value.trim().toUpperCase();
        if (trimmed === "NA") return "NA";
        const words = trimmed.split(/\s+/);
        if (words.length === 1) {
          return words[0].charAt(0);
        }
        return words.map((w) => w.charAt(0)).join("");
      };

      // Prepare payload matching mobile app ApplicationforPassport.tsx
      const payload: any = {
        is_draft: false,
        errorMsg: "",
        placeOfPosting: formData.placeOfPosting || "",
        nOCAppliedFor: formData.nocAppliedFor || "0",
        durationOfVisit: formData.durationOfVisit || "",
        tentativeDate: formData.tentativeDate
          ? moment(formData.tentativeDate, ["YYYY-MM-DD", "DD-MM-YYYY", "DD.MM.YYYY"]).format("YYYY-MM-DD")
          : currentDateYMD,
        presentAddress: formData.presentAddress || "",
        permanentAddress: formData.permanentAddress || "",
        contactNumber: formData.contactNumber || "",
        remarks: "",
        dnARPenaltyEffecti: "",
        dnARStatus: getFirstLetter(formData.dnARStatus) || "",
        vigilancePending: getFirstLetter(formData.vigilancePending) || "N",
        dnARPending: getFirstLetter(formData.dnARPending) || "N",
        eMailAddress: formData.email || "",
        pendingCase: getFirstLetter(formData.pendingCase) || "N",
        passportType: getFirstLetter(formData.passportType) || "",
        passportNumber: formData.passportNumber || "",
        placeOfVisitNOC: formData.placeOfVisitNOC || "",
        purposeOfVisit: formData.purposeOfVisit || "",
        departmentName: formData.department || "",
        employeeName: formData.name || "",
        hRemarks: "",
        fatherName: formData.fatherName || "",
      };

      console.log("Payload:", payload);

      // Add ID for edit mode
      if (isEditMode && id) {
        payload.id = parseInt(id);
      }

      const result = await dispatch(
        createOrUpdateNocPassport(payload)
      ).unwrap();

      console.log("Result:", result);

      const rawMsg =
        result?.data?.data?.message ||
        result?.data?.message ||
        result?.message;

      const successMessage =
        rawMsg &&
          rawMsg.toLowerCase() !== "noc saved" &&
          rawMsg.toLowerCase() !== "saved successfully" &&
          rawMsg.toLowerCase() !== "created successfully"
          ? rawMsg
          : isEditMode
            ? "NOC application updated successfully!"
            : "Created successfully!";

      setResponseMessage(successMessage);
      setResponseType("success");

      setTimeout(() => {
        setShowLoadingModal(false);
        setShowResponseModal(true);
      }, 500);
    } catch (error: any) {
      console.error("Failed to submit NOC application:", error);

      // Show specific error message in modal based on error type and API response
      let errorMessage = "Failed to submit application. Please try again.";

      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.error) {
        errorMessage = error.error;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setResponseMessage(errorMessage);
      setResponseType("error");
      setShowLoadingModal(false);
      setShowResponseModal(true);
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    // Validate required fields for draft
    if (!formData.nocAppliedFor || !formData.nocAppliedFor.trim()) {
      toast.error("NOC Applied For is required to save as draft");
      return;
    }

    if (!formData.placeOfPosting || !formData.placeOfPosting.trim()) {
      toast.error("Place of posting is required to save as draft");
      return;
    }

    if (formData.contactNumber && !/^\d{10}$/.test(formData.contactNumber)) {
      toast.error("Contact number must be exactly 10 digits");
      return;
    }

    setIsSubmitting(true);
    setShowLoadingModal(true);
    setLoadingMessage(isEditMode ? "Updating draft..." : "Saving draft...");

    try {
      const currentDateYMD = moment().format("YYYY-MM-DD");

      const getFirstLetter = (value: string): string => {
        if (!value) return "";
        const trimmed = value.trim().toUpperCase();
        if (trimmed === "NA") return "NA";
        const words = trimmed.split(/\s+/);
        if (words.length === 1) {
          return words[0].charAt(0);
        }
        return words.map((w) => w.charAt(0)).join("");
      };

      // Prepare payload matching mobile app ApplicationforPassport.tsx
      const payload: any = {
        is_draft: true,
        errorMsg: "",
        placeOfPosting: formData.placeOfPosting || "",
        nOCAppliedFor: formData.nocAppliedFor || "0",
        durationOfVisit: formData.durationOfVisit || "",
        tentativeDate: formData.tentativeDate
          ? moment(formData.tentativeDate, ["YYYY-MM-DD", "DD-MM-YYYY", "DD.MM.YYYY"]).format("YYYY-MM-DD")
          : currentDateYMD,
        presentAddress: formData.presentAddress || "",
        permanentAddress: formData.permanentAddress || "",
        contactNumber: formData.contactNumber || "",
        remarks: "",
        dnARPenaltyEffecti: "",
        dnARStatus: getFirstLetter(formData.dnARStatus) || "",
        vigilancePending: getFirstLetter(formData.vigilancePending) || "N",
        dnARPending: getFirstLetter(formData.dnARPending) || "N",
        eMailAddress: formData.email || "",
        pendingCase: getFirstLetter(formData.pendingCase) || "N",
        passportType: getFirstLetter(formData.passportType) || "",
        passportNumber: formData.passportNumber || "",
        placeOfVisitNOC: formData.placeOfVisitNOC || "",
        purposeOfVisit: formData.purposeOfVisit || "",
        departmentName: formData.department || "",
        employeeName: formData.name || "",
        hRemarks: "",
        fatherName: formData.fatherName || "",
      };

      // Add ID for edit mode
      if (isEditMode && id) {
        payload.id = parseInt(id);
      }

      await dispatch(createOrUpdateNocPassport(payload)).unwrap();
      toast.success(
        isEditMode
          ? "Draft updated successfully!"
          : "Draft submitted successfully!"
      );

      // Redirect to list page after showing toast
      setTimeout(() => {
        navigate(routes.noc.passport);
      }, 1500);
    } catch (error: any) {
      console.log("Error saving draft:", error);
      const errorMessage =
        error?.message || error?.error || error || "Failed to save draft";
      toast.error(errorMessage);
      setShowLoadingModal(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.noc.passport}
                className="text-primary text-decoration-none me-3"
              >
                <i className="ti ti-arrow-left fs-24"></i>
              </Link>
              <h3 className="page-title mb-0">
                {isEditMode ? "Edit" : "Application for"} NOC for Passport /
                Visa
              </h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.passport}>Passport / Visa</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {isEditMode ? "Edit Request" : "Create Request"}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Overview Modal */}
        {showChecklist && (
          <>
            {/* Backdrop */}
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
              style={{ zIndex: 1040 }}
            />

            {/* Overview Modal */}
            <div
              className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
              style={{
                zIndex: 1050,
                width: "90%",
                maxWidth: "600px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              {/* Modal Header */}
              <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
                <h5 className="mb-0 text-dark fw-bold">
                  NOC for Passport overview
                </h5>
                <button
                  className="btn btn-link text-danger text-decoration-none p-0"
                  onClick={() => setShowChecklist(false)}
                >
                  <i className="ti ti-x fs-24"></i>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4" style={{ lineHeight: "1.6" }}>
                <div className="text-dark">
                  <p className="mb-3">
                    <strong>1.</strong> {tBoth("noc_passport_point_1")}
                  </p>

                  <p className="mb-3">
                    <strong>2.</strong> {tBoth("noc_passport_point_2")}
                  </p>

                  <p className="mb-3">
                    <strong>3.</strong> {tBoth("noc_passport_point_3")}
                  </p>

                  <p className="mb-3">
                    <strong>4.</strong> {tBoth("noc_passport_point_4")}
                  </p>
                </div>
              </div>

              {/* OK Button */}
              <div className="d-flex justify-content-end p-4 pt-0">
                <button
                  type="button"
                  className="btn text-white px-5 py-2 fw-bold"
                  style={{
                    backgroundColor: "#B85450",
                    borderRadius: "25px",
                    fontSize: "16px",
                  }}
                  onClick={() => setShowChecklist(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </>
        )}

        {/* Main Form */}
        <div className="card">
          <div className="card-body">
            {isLoadingProfile || isLoadingPassportData ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span className="ms-2">
                  {isLoadingProfile
                    ? "Loading your profile data..."
                    : "Loading passport data..."}
                </span>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Section 1: Employee Details */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Employee Details</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {/* Name */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Name{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      {/* Employee No. */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Employee No. <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="employeeNo"
                            value={formData.employeeNo}
                            onChange={handleInputChange}
                            style={{ backgroundColor: "#f8f9fa" }}
                            readOnly
                          />
                        </div>
                      </div>

                      {/* Father's Name */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Father's Name{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="fatherName"
                            value={formData.fatherName}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter father's full name"
                          />
                        </div>
                      </div>

                      {/* Designation */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Designation <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="designation"
                            value={formData.designation}
                            onChange={handleInputChange}
                            style={{ backgroundColor: "#f8f9fa" }}
                            readOnly
                          />
                        </div>
                      </div>

                      {/* Date of Joining */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Date Of Joining{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="dateOfJoining"
                            value={formData.dateOfJoining}
                            onChange={handleInputChange}
                            style={{ backgroundColor: "#f8f9fa" }}
                            readOnly
                          />
                        </div>
                      </div>

                      {/* Employment Status */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Employment Status{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="employmentStatus"
                            value={formData.employmentStatus}
                            onChange={handleInputChange}
                            style={{ backgroundColor: "#f8f9fa" }}
                            readOnly
                          />
                        </div>
                      </div>

                      {/* Place Of Posting */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Place Of Posting{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="placeOfPosting"
                            value={formData.placeOfPosting}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter current place of posting"
                          />
                        </div>
                      </div>

                      {/* Department */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Department <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      {/* Contact Number */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Contact Details (Mobile No.){" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="tel"
                            className="form-control"
                            name="contactNumber"
                            value={formData.contactNumber}
                            onChange={handleInputChange}
                            maxLength={10}
                            placeholder="Enter 10-digit mobile number"
                            required
                          />
                        </div>
                      </div>

                      {/* Email ID */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Email ID <span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      {/* Date of Birth */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Date of Birth <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="dateOfBirth"
                            value={formData.dateOfBirth}
                            onChange={handleInputChange}
                            style={{ backgroundColor: "#f8f9fa" }}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Application Details */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Application Details</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {/* Present Address */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Present Address{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className="form-control"
                            name="presentAddress"
                            value={formData.presentAddress}
                            onChange={handleInputChange}
                            required
                            rows={3}
                            maxLength={150}
                            placeholder="Enter your current address"
                          ></textarea>
                          <small className="text-muted">
                            {formData.presentAddress.length}/150 characters
                          </small>
                        </div>
                      </div>

                      {/* Permanent Address */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Permanent Address{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className="form-control"
                            name="permanentAddress"
                            value={formData.permanentAddress}
                            onChange={handleInputChange}
                            required
                            rows={3}
                            maxLength={150}
                            placeholder="Enter your permanent address"
                          ></textarea>
                          <small className="text-muted">
                            {formData.permanentAddress.length}/150 characters
                          </small>
                        </div>
                      </div>

                      {/* NOC applied for */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            NOC applied for{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select"
                            name="nocAppliedFor"
                            value={formData.nocAppliedFor}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select NOC Type</option>
                            <option value="0">Fresh Passport</option>
                            <option value="1">Renewal of Passport</option>
                            <option value="2">Visa</option>
                          </select>
                        </div>
                      </div>

                      {/* Conditional Passport Number field - Show only for Renewal */}
                      {formData.nocAppliedFor === "1" && (
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="form-label text-dark fw-medium">
                              Passport No.
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="passportNumber"
                              value={formData.passportNumber}
                              onChange={handleInputChange}
                              required
                              placeholder="Enter passport number"
                            />
                          </div>
                        </div>
                      )}

                      {/* Conditional Visa fields */}
                      {formData.nocAppliedFor === "2" && (
                        <>
                          {/* Type of Passport */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Type of Passport
                              </label>
                              <select
                                className="form-select"
                                name="passportType"
                                value={formData.passportType}
                                onChange={handleInputChange}
                                required
                              >
                                <option value="">Select Type</option>
                                <option value="O">Official</option>
                                <option value="R">Regular</option>
                                <option value="D">Diplomatic</option>
                              </select>
                            </div>
                          </div>

                          {/* Passport No. for Visa - next to Type of Passport */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Passport No.
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="passportNumber"
                                value={formData.passportNumber}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter passport number"
                              />
                            </div>
                          </div>

                          {/* Countries to be visited */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Countries to be visited
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="placeOfVisitNOC"
                                value={formData.placeOfVisitNOC}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter countries to be visited"
                              />
                            </div>
                          </div>

                          {/* Purpose of Visit */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Purpose of Visit
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="purposeOfVisit"
                                value={formData.purposeOfVisit}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter purpose of visit"
                              />
                            </div>
                          </div>

                          {/* Tentative date of visit */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Tentative date of visit
                              </label>
                              <ConfigProvider
                                theme={{
                                  token: {
                                    colorPrimary: "#1890ff",
                                  },
                                }}
                              >
                                <DatePicker
                                  className="form-control"
                                  value={
                                    formData.tentativeDate
                                      ? dayjs(formData.tentativeDate)
                                      : null
                                  }
                                  onChange={(date) => {
                                    handleInputChange({
                                      target: {
                                        name: "tentativeDate",
                                        value: date
                                          ? date.format("YYYY-MM-DD")
                                          : "",
                                      },
                                    } as any);
                                  }}
                                  format="DD-MM-YYYY"
                                  placeholder="Select date"
                                  style={{ width: "100%" }}
                                />
                              </ConfigProvider>
                            </div>
                          </div>

                          {/* Duration of visit */}
                          <div className="col-md-6">
                            <div className="form-group mb-3">
                              <label className="form-label text-dark fw-medium">
                                Duration of visit
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="durationOfVisit"
                                value={formData.durationOfVisit}
                                onChange={handleInputChange}
                                required
                                placeholder="e.g., 15 days, 2 weeks, 1 month"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section 3: Declaration Questions */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Declaration Questions</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {/* Court/Police/Vigilance case */}
                      <div className="col-md-12">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Are you involved in any court/ Police/ Vigilance
                            case for which your presence is required?{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="mt-2">
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="pendingCase"
                                id="court_case_yes"
                                value="Yes"
                                checked={formData.pendingCase === "Yes"}
                                onChange={handleInputChange}
                              />
                              <label
                                className="form-check-label text-dark fw-medium"
                                htmlFor="court_case_yes"
                              >
                                Yes
                              </label>
                            </div>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="pendingCase"
                                id="court_case_no"
                                value="No"
                                checked={formData.pendingCase === "No"}
                                onChange={handleInputChange}
                              />
                              <label
                                className="form-check-label text-dark fw-medium"
                                htmlFor="court_case_no"
                              >
                                No
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* D&AR pending */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Whether any D&AR pending?{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="mt-2">
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="dnARPending"
                                id="dsar_yes"
                                value="Yes"
                                checked={formData.dnARPending === "Yes"}
                                onChange={handleInputChange}
                              />
                              <label
                                className="form-check-label text-dark fw-medium"
                                htmlFor="dsar_yes"
                              >
                                Yes
                              </label>
                            </div>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="dnARPending"
                                id="dsar_no"
                                value="No"
                                checked={formData.dnARPending === "No"}
                                onChange={handleInputChange}
                              />
                              <label
                                className="form-check-label text-dark fw-medium"
                                htmlFor="dsar_no"
                              >
                                No
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Vigilance pending */}
                      <div className="col-md-6">
                        <div className="form-group mb-3">
                          <label className="form-label text-dark fw-medium">
                            Whether any vigilance pending?{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="mt-2">
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="vigilancePending"
                                id="vigilance_yes"
                                value="Yes"
                                checked={formData.vigilancePending === "Yes"}
                                onChange={handleInputChange}
                              />
                              <label
                                className="form-check-label text-dark fw-medium"
                                htmlFor="vigilance_yes"
                              >
                                Yes
                              </label>
                            </div>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="vigilancePending"
                                id="vigilance_no"
                                value="No"
                                checked={formData.vigilancePending === "No"}
                                onChange={handleInputChange}
                              />
                              <label
                                className="form-check-label text-dark fw-medium"
                                htmlFor="vigilance_no"
                              >
                                No
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Note Section */}
                <div className="alert alert-warning mb-4">
                  <strong>Note:</strong> Employee should submit a good quality
                  passport size photograph in HR department after final approval
                  in ESS.
                </div>

                {/* Declaration Section */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Declaration</h5>
                  </div>
                  <div className="card-body">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="declaration"
                        id="declaration"
                        checked={formData.declaration}
                        onChange={handleInputChange}
                        required
                      />
                      <label
                        className="form-check-label text-dark"
                        htmlFor="declaration"
                      >
                        I certify that I am not involved in any
                        court/police/vigilance case and there is no restriction
                        placed by any authority on foreign travel.
                      </label>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex justify-content-center gap-3 mt-4">
                  <Link
                    to={routes.noc.passport}
                    className="btn btn-outline-secondary px-4"
                  >
                    <i className="ti ti-arrow-left me-1"></i>
                    Cancel
                  </Link>
                  <button
                    type="button"
                    className="btn btn-secondary px-4"
                    onClick={handleSaveDraft}
                    disabled={isSubmitting}
                  >
                    <i className="ti ti-device-floppy me-1"></i>
                    Save as Draft
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={isSubmitting}
                  >
                    <i className="ti ti-send me-1"></i>
                    Submit
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Full Page Loader */}
      <FullPageLoader
        show={showLoadingModal}
        message={loadingMessage}
      />

      {/* Response Modal */}
      {showResponseModal && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
            style={{ zIndex: 2000 }}
          />

          {/* Response Modal */}
          <div
            className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
            style={{
              zIndex: 2001,
              width: "90%",
              maxWidth: "500px",
            }}
          >
            {/* Modal Header */}
            <div
              className={`d-flex align-items-center justify-content-between p-3 border-bottom ${responseType === "success" ? "bg-success" : "bg-info"
                }`}
            >
              <h5 className="mb-0 text-white fw-bold">
                {responseType === "success" ? (
                  <>
                    <i className="ti ti-check-circle me-2"></i>
                    Success
                  </>
                ) : (
                  <>
                    <i className="ti ti-info-circle me-2"></i>
                    Notice
                  </>
                )}
              </h5>
              <button
                className="btn btn-link text-white text-decoration-none p-0"
                onClick={() => {
                  setShowResponseModal(false);
                  if (responseType === "success") {
                    navigate(routes.noc.passport);
                  }
                }}
              >
                <i className="ti ti-x fs-20"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4">
              <div
                className={`p-3 rounded ${responseType === "success"
                  ? "bg-success bg-opacity-10"
                  : "bg-info bg-opacity-10"
                  }`}
              >
                <p className="mb-0 text-dark">{responseMessage}</p>
              </div>

              <div className="d-flex justify-content-center mt-4">
                <button
                  type="button"
                  className={`btn ${responseType === "success" ? "btn-success" : "btn-info"
                    } px-5`}
                  onClick={() => {
                    setShowResponseModal(false);
                    if (responseType === "success") {
                      navigate(routes.noc.passport);
                    }
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PassportCreate;
